import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MoshLogo from 'src/components/MoshLogo';
import MoshyLogo from 'src/components/MoshyLogo';
import { useContext, useRef, useState } from 'react';
import useOutsideClick from 'src/hooks/useOutsideClick';
import { QuizContext } from '../Quiz.context';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { Vertical } from 'src/constants/Verticals';
import { usePathname, useSearchParams } from 'next/navigation';
import { useUser } from 'src/hooks/User/useUser';
import {
    faCircle,
    faMessages,
    faArrowRightFromBracket,
    faSpinnerThird,
    faXmark,
    faArrowLeftFromBracket,
} from '@fortawesome/pro-solid-svg-icons';
import { Button, IconButton } from '@get-mosh/msh-shared';
import PortalRoutes from 'src/constants/PortalRoutes';
import { env, isMosh } from 'src/env.mjs';
import { getQuizBaseUrl } from 'src/helpers/quiz';
import { useMedia } from 'use-media';
import Routes from 'src/constants/Routes';
import { Modal } from '@get-mosh/msh-shared';
import Link from 'next/link';

export const QUIZ_HEADER_TYPES = ['a', 'b'];
export const QUIZ_PROGRESS_TYPES = {
    percentage: 'percentage',
    fraction: 'fraction',
};

type ProgressBarProps = {
    currentValue: number;
    maxValue: number;
    progressType?: 'percentage' | 'fraction' | undefined;
};

const ProgressBar = ({
    currentValue,
    maxValue,
    progressType,
}: ProgressBarProps) => (
    <div
        className={classNames(
            'fixed inset-x-0 top-14 z-20 flex w-screen items-center gap-4 p-4', // Fixed position, full width, overlaps content
            'lg:relative lg:left-auto lg:right-auto lg:top-auto lg:w-[355px] lg:p-0', // Adjust for larger screens
        )}
    >
        {progressType === QUIZ_PROGRESS_TYPES.percentage ? (
            <label className="p2 highlight">
                {`${Math.floor((currentValue / maxValue) * 100)}%`}
            </label>
        ) : progressType === QUIZ_PROGRESS_TYPES.fraction ? (
            <label className="p2 highlight">
                {`${currentValue}/${maxValue}`}
            </label>
        ) : null}
        <progress
            className={classNames(
                'h-1.5 flex-grow appearance-none [&::-webkit-progress-bar]:rounded-lg [&::-webkit-progress-value]:rounded-lg',
                'rounded-lg [&::-moz-progress-bar]:rounded-lg',
                {
                    '[&::-webkit-progress-bar]:bg-grey-200 [&::-moz-progress-bar]:bg-[#6fab93] [&::-webkit-progress-value]:bg-[#6fab93]':
                        isMosh,
                    '[&::-moz-progress-bar]:bg-blue-dark [&::-webkit-progress-bar]:bg-blue-light [&::-webkit-progress-value]:bg-blue-dark':
                        !isMosh,
                },
            )}
            value={currentValue}
            max={maxValue}
        />
    </div>
);

const getInitials = (
    firstName: string | null,
    lastName: string | null,
): string => {
    const initials = `${firstName?.charAt(0).toUpperCase() ?? ''}${lastName?.charAt(0).toUpperCase() ?? ''}`;
    return initials;
};

const QuizMenuLayout = ({
    firstName,
    lastName,
    onClose,
    handleClickLink,
}: {
    firstName: string;
    lastName: string;
    onClose: () => void;
    handleClickLink: (args: { label: string }) => void;
}) => (
    <div className={classNames('flex flex-col')}>
        <div
            className={classNames(
                'flex items-center justify-between px-4 pt-4',
            )}
        >
            <div className="relative">
                <FontAwesomeIcon
                    icon={faCircle}
                    className={
                        isMosh
                            ? 'text-green text-lg'
                            : 'text-blue-extra-light text-lg'
                    }
                />
                <span
                    className={classNames(
                        'p1 highlight absolute inset-0 flex items-center justify-center',
                    )}
                >
                    {getInitials(firstName, lastName)}
                </span>
            </div>
            <div className="ml-2">
                <p className="text-grey-400 m-0 p-0 text-xs">Logged in as</p>
                <p className="p1 highlight m-0 p-0 text-base font-medium">
                    {`${firstName} ${lastName}`}
                </p>
            </div>
            <button onClick={onClose} className="-mt-5 ml-auto">
                <FontAwesomeIcon className="text-grey-400" icon={faXmark} />
            </button>
        </div>
        {/* Static Content */}
        <div className="flex flex-col p-4">
            <a
                href={PortalRoutes.NOTIFICATIONS}
                className="p1 highlight bg-grey-100 rounded-lg p-4"
                onClick={() => handleClickLink({ label: 'Go to my account' })}
            >
                Go to my account
            </a>
            <a
                href={PortalRoutes.SUPPORT}
                className="p1 highlight bg-grey-100 mb-4 mt-2 rounded-lg p-4"
                target="_blank"
                onClick={() =>
                    handleClickLink({ label: 'Frequently asked questions' })
                }
            >
                Frequently asked questions
            </a>
            <div className="flex items-center justify-between">
                <a
                    href={PortalRoutes.CONTACT}
                    className="flex items-center space-x-2"
                    target="_blank"
                    onClick={() => handleClickLink({ label: 'Contact us' })}
                >
                    <p className="p1 highlight text-grey-400 p-0">Contact us</p>
                    <FontAwesomeIcon
                        icon={faMessages}
                        className="text-grey-400"
                    />
                </a>
                <a
                    onClick={() => handleClickLink({ label: 'Logout' })}
                    href={PortalRoutes.SIGN_OUT}
                    className="flex items-center space-x-2"
                >
                    <p className="p1 highlight text-grey-400 p-0">Logout</p>
                    <FontAwesomeIcon
                        icon={faArrowRightFromBracket}
                        className="text-grey-400"
                    />
                </a>
            </div>
        </div>
    </div>
);
type QuizModalProps = {
    firstName: string;
    lastName: string;
    isOpen: boolean;
    onClose: () => void;
    overflow?: boolean;
    handleClickLink: (args: { label: string }) => void;
};
const QuizModal = ({
    firstName,
    lastName,
    isOpen,
    onClose,
    overflow,
    handleClickLink,
}: QuizModalProps) => (
    <div
        className={classNames(
            '-z-10 w-full opacity-0 transition-opacity', // shared classes
            'fixed right-0 top-0 p-3', // mobile classes
            'lg:fixed lg:right-[7.5rem] lg:top-4 lg:max-w-[20rem] lg:p-0',
            isOpen
                ? 'top-0 z-30 opacity-100 lg:top-4'
                : '-top-full -z-10 opacity-0',
            overflow ? 'max-h-[90vh]' : '',
        )}
    >
        <div className="border-grey-200 h-full w-full rounded-lg border bg-white shadow-[0_25px_40px_-15px_rgba(19,40,18,0.15)]">
            <QuizMenuLayout
                firstName={firstName}
                lastName={lastName}
                onClose={onClose}
                handleClickLink={handleClickLink}
            />
        </div>
    </div>
);

type QuizHeaderProps = {
    progressType?: 'percentage' | 'fraction' | undefined;
    variant?: 'dark' | '';
};
const QuizHeader = ({ progressType, variant }: QuizHeaderProps) => {
    const pathname = usePathname();
    const {
        quizType,
        quizFlow,
        quizName,
        quizVersion,
        currentQuestionIndex,
        currentQuestion,
        questionList,
        quizToken,
        quizSubVersion,
    } = useContext(QuizContext);
    const searchParams = useSearchParams();
    const subscriptionId = searchParams?.get('subscription_id') || '';
    const { questionText } = currentQuestion || {};
    const progressCurrent = Array.isArray(questionList)
        ? currentQuestionIndex + 1
        : 0;
    const progressMax = Array.isArray(questionList) ? questionList.length : 0;
    const navRef = useRef<HTMLElement | null>(null);
    const [questionListIsVisible, setQuestionListIsVisible] = useState(false);
    const [accountIsVisible, setAccountIsVisible] = useState(false);
    const { data: user, isLoading } = useUser();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const quizBaseUrl = getQuizBaseUrl({
        quizFlow,
        quizType,
        quizToken: quizToken ?? '',
        quizVersion: quizVersion ?? '',
        quizSubVersion: quizSubVersion ?? '',
        subscriptionId,
    });
    // Menu handlers
    const onClose = () => {
        setQuestionListIsVisible(false);
        setAccountIsVisible(false);
    };
    const handleClickOutside = () => {
        if (questionListIsVisible || accountIsVisible) onClose();
    };
    useOutsideClick(navRef, handleClickOutside);
    const isDark = variant === 'dark' && isMosh;
    const handleClickLink = ({
        label,
        destination_url,
    }: {
        label: string;
        destination_url?: string;
    }) => {
        trackClick(EventNames.QUIZ_BUTTON_CLICKED, {
            quiz_name: `${quizName} - ${quizVersion}`,
            vertical: Vertical[quizType.toUpperCase()],
            label: label,
            question_name: questionText,
            url: pathname,
            destination_url: destination_url,
        });
    };

    const getLogo = () => (
        <div
            className={classNames(
                'mx-auto flex basis-full items-center gap-4 pt-2 lg:pt-5',
                { 'lg:-mt-5': isDark },
            )}
        >
            <Link
                href={Routes.HOME}
                onClick={() => {
                    handleClickLink({
                        label: 'mosh logo',
                        destination_url: Routes.HOME,
                    });
                }}
            >
                {isMosh ? (
                    <MoshLogo
                        className="max-w-[60px]"
                        variant={isDark ? 'extra-light' : ''}
                    />
                ) : (
                    <MoshyLogo className="max-w-[60px]" />
                )}
            </Link>
        </div>
    );
    const getProgress = () => (
        <ProgressBar
            currentValue={progressCurrent}
            maxValue={progressMax}
            progressType={progressType}
        />
    );
    const isSmallScreen = useMedia({ maxWidth: '640px' });

    return (
        <nav id="mainHeader" className="w-full" ref={navRef}>
            <div
                className={classNames(
                    'items-center justify-center gap-5', // shared classes
                    'mx-auto flex p-3 pb-9', // mobile classes
                    'lg:flex lg:h-[6.25rem] lg:justify-between lg:px-[7.5rem] lg:py-[18px]',
                )}
            >
                <div
                    className={
                        'flex flex-nowrap items-center gap-2 whitespace-nowrap'
                    }
                >
                    <IconButton
                        onClick={() => {
                            {
                                handleClickLink({ label: 'Back' });
                                setIsModalOpen(true);
                            }
                        }}
                        icon={faArrowLeftFromBracket}
                        variant={isDark ? 'tertiaryLight' : 'tertiary'}
                    />
                </div>

                <div
                    className={classNames(
                        'flex flex-grow flex-col items-center justify-center gap-4',
                        user ? 'ml-0' : 'ml-7 lg:ml-9',
                    )}
                >
                    {getLogo()}
                    {variant !== 'dark' && getProgress()}
                </div>
                <div className="flex justify-end">
                    {/* Check if login is active and show the appropriate button */}
                    {isLoading ? (
                        <div className="flex items-center justify-center">
                            <FontAwesomeIcon
                                icon={faSpinnerThird}
                                className={'fa-spin h-10 w-10'}
                            />
                        </div>
                    ) : user ? (
                        <div className="flex items-center justify-center">
                            <button
                                onClick={() => {
                                    setAccountIsVisible(true);
                                    handleClickLink({ label: 'Avatar' });
                                }}
                                className={classNames(
                                    'p1 highlight h-10 w-10 rounded-full text-base font-medium',
                                    isMosh ? 'bg-green' : 'bg-blue-extra-light',
                                )}
                            >
                                {getInitials(user.firstName, user.lastName)}
                            </button>
                        </div>
                    ) : (
                        <Button
                            as={Link}
                            href={`${Routes.LOGIN}?redirect_url=${
                                env.NEXT_PUBLIC_BASE_PATH
                            }${encodeURIComponent(quizBaseUrl)}`}
                            className={classNames('flex items-center')}
                            onClick={() => {
                                handleClickLink({
                                    label: 'Log in',
                                    destination_url: Routes.LOGIN,
                                });
                            }}
                            text="Log in"
                            variant={isDark ? 'secondaryLight' : 'secondary'}
                            size={isSmallScreen ? 'small' : 'regular'}
                        />
                    )}
                </div>
            </div>
            <QuizModal
                firstName={`${user?.firstName || ''}`}
                lastName={`${user?.lastName || ''}`}
                isOpen={accountIsVisible}
                onClose={onClose}
                handleClickLink={handleClickLink}
            />
            <Modal
                className="!border-0 !p-0"
                isOpen={isModalOpen}
                onModalClose={(value) => {
                    setIsModalOpen(value || false);
                }}
                shouldCloseOnOverlayClick={true}
                onRequestClose={() => setIsModalOpen(false)}
            >
                {user ? (
                    <div
                        className={classNames(
                            'flex flex-col gap-2 p-4 sm:w-[375px] md:h-auto md:max-w-[360px]',
                        )}
                    >
                        <h4 className="text-left">
                            Your progress will be saved
                        </h4>
                        <span
                            className={classNames(
                                'text-left text-base font-normal',
                                isMosh ? 'text-green-dark' : 'text-grey-400',
                            )}
                        >
                            You can continue this quiz at any time by logging in
                            to your account.
                        </span>
                        <Button
                            as="a"
                            href={Routes.HOME}
                            className="w-full"
                            onClick={() => {
                                handleClickLink({
                                    label: 'Exit quiz',
                                    destination_url: Routes.HOME,
                                });
                            }}
                            text="Exit quiz"
                            variant="primary"
                        />
                        <Button
                            className="w-full"
                            onClick={() => {
                                setIsModalOpen(false);
                            }}
                            text="Cancel"
                            variant="secondary"
                        />
                    </div>
                ) : (
                    <div
                        className={classNames(
                            'flex flex-col gap-2 p-4 sm:w-[375px] md:h-auto md:max-w-[360px]',
                        )}
                    >
                        <h4 className="text-left">Save your progress?</h4>
                        <span
                            className={classNames(
                                'text-left text-base font-normal',
                                isMosh ? 'text-green-dark' : 'text-grey-400',
                            )}
                        >
                            By creating an account with us, you can resume your
                            quiz at any time.
                        </span>
                        <Button
                            as={Link}
                            href={`${Routes.CREATE_ACCOUNT}?redirect_url=${encodeURIComponent(
                                window.location.href,
                            )}`}
                            className="w-full"
                            onClick={() => {
                                setIsModalOpen(false);
                                handleClickLink({
                                    label: 'Create an account',
                                    destination_url: `${Routes.CREATE_ACCOUNT}?redirect_url=${encodeURIComponent(
                                        window.location.href,
                                    )}`,
                                });
                            }}
                            text="Create an account"
                            variant="primary"
                        />
                        <Button
                            as="a"
                            href={Routes.HOME}
                            className="w-full"
                            onClick={() => {
                                setIsModalOpen(false);
                                handleClickLink({
                                    label: 'Abandon quiz progress',
                                    destination_url: Routes.HOME,
                                });
                            }}
                            text="Abandon quiz progress"
                            variant="secondary"
                        />
                    </div>
                )}
            </Modal>
        </nav>
    );
};

export default QuizHeader;
